/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import cn from "classnames";
import { ArrowLeft, ArrowRight } from "components/icons";
import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import useWindowDimensions from "utils/useWindowDimensions";
import s from "./Pagination.module.css";

const NextButton = ({ isPopupPagination = false }) => (
  <div
    className={`flex items-center space-x-1 ${
      isPopupPagination ? "h-[40px] w-[40px] justify-center" : ""
    }`}
  >
    {/* <Text className="hidden md:inline font-semibold">Next</Text> */}
    <ArrowRight size="sm" className="stroke-current text-current stroke-2 " />
  </div>
);

const PrevButton = ({ isPopupPagination = false }) => (
  <div
    className={`flex items-center space-x-1 ${
      isPopupPagination ? "h-[40px] w-[40px] justify-center" : ""
    }`}
  >
    <ArrowLeft size="sm" className="stroke-current text-current stroke-2" />
    {/* <Text className="hidden md:inline font-semibold">Previous</Text> */}
  </div>
);

interface IPagination {
  pages: number;
  note?: React.ReactNode;
  base?: string;
  current?: number | string;
  pageRange?: number;
  isPopupPagination?: boolean;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
  position?: string;
}

const Pagination = ({
  pages = 10,
  base,
  note = "",
  current = 1,
  isPopupPagination = false,
  pageRange,
  onPageChange,
  position,
}: IPagination) => {
  const navigate = useNavigate();

  const dimensions = useWindowDimensions();
  const [manualPageRange, setManualPageRange] = useState(3);

  // const handlePageRange = (isLaptop: boolean, isMobile: boolean) => {
  //   if (isLaptop) {
  //     return 7;
  //   }
  //   if (isMobile) {
  //     return 3;
  //   }
  //   return 4;
  // };
  useEffect(() => {
    if (pageRange) {
      return;
    }

    if (dimensions.width < 450) {
      setManualPageRange(1);
    }

    if (dimensions.width > 450 && dimensions.width < 800) {
      setManualPageRange(3);
    }

    if (dimensions.width > 800) {
      if (!note) {
        setManualPageRange(7);
      } else {
        setManualPageRange(5);
      }
    }
  }, [dimensions.width]);

  const [searchParams] = useSearchParams();
  return (
    <div
      className={cn(
        "w-full flex flex-col gap-y-2 md:flex-row justify-between mt-4 test",
        {
          "flex-col space-y-2 ml-2  !mt-0": position === "col",
          // "flex-col space-y-2 !items-center !mt-0": position === "col",
        },
      )}
    >
      {pages > 1 && (
        <div
          className={cn(s.note, "md:hidden flex justify-between pt-2", {
            // "!justify-start !w-full ml-5": position === "col",
          })}
        >
          {note}
        </div>
      )}

      <div
        className={isPopupPagination ? s.popupPaginationContainer : s.container}
      >
        {pages > 1 && (
          <div className={cn(s.note, "hidden md:flex")}>{note}</div>
        )}

        <div className={s.spacing}>
          {pages > 1 && (
            <ReactPaginate
              breakLabel="..."
              containerClassName={`${s.spacing} ${
                isPopupPagination ? "space-x-0" : ""
              }`}
              pageClassName={
                isPopupPagination ? s.popupPaginationPageItem : s.pageItem
              }
              pageLinkClassName={s.pageLink}
              breakClassName={
                isPopupPagination ? s.popupPaginationPageItem : s.pageItem
              }
              breakLinkClassName={`${s.pageLink} ${
                isPopupPagination ? "border-r-[1px] border-[#3C3C3C]" : ""
              }`}
              activeClassName={`${s.active}  ${
                isPopupPagination ? "bg-primary hover:bg-primary" : ""
              }`}
              nextClassName={isPopupPagination ? s.popupPaginationNext : s.next}
              previousClassName={
                isPopupPagination ? s.popupPaginationPrev : s.prev
              }
              disabledClassName={s.disabled}
              onPageChange={({ selected: index }) => {
                if (onPageChange) {
                  const canContinue = onPageChange(index + 1);
                  // @ts-ignore
                  if (canContinue === false) return;
                }

                if (!base) return;
                const page = index + 1;
                const newSearchParams = createSearchParams(searchParams);
                navigate({
                  pathname: `${base}/${page}`,
                  search: newSearchParams.toString(),
                });
              }}
              pageRangeDisplayed={
                pageRange && pageRange > 1 ? pageRange : manualPageRange
              }
              marginPagesDisplayed={1}
              pageCount={Math.ceil(pages)}
              nextLabel={<NextButton isPopupPagination={isPopupPagination} />}
              previousLabel={
                <PrevButton isPopupPagination={isPopupPagination} />
              }
              renderOnZeroPageCount={() => null}
              forcePage={(() => {
                const number = Number(current);
                if (isNaN(number)) {
                  return 0;
                }

                if (number > pages) {
                  return Math.ceil(pages - 1); // this was a bug in pagination cmp
                }

                return number - 1;
              })()}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Pagination;
